import { forwardRef, type ReactNode, type CSSProperties } from "react";
import type {
  BackgroundSettings,
  CardFormat,
  HeaderSettings,
  ScrimSettings,
} from "./types";
import { CARD_FORMATS } from "./types";

interface FbkCardShellProps {
  format: CardFormat;
  children: ReactNode;
  /** Footer left text — e.g. "Build yours · fansballknowledge.com/news" */
  footerLeft?: string;
  /** Footer right text — e.g. "#WC2026 · #FBK" */
  footerRight?: string;
  /** Header overrides (wordmark, logo, pill text, hide). */
  header?: HeaderSettings;
  /** Optional user-supplied background image overlaying the brand gradient */
  background?: BackgroundSettings;
  /**
   * Override the base brand gradient with a custom CSS background value.
   * Gold glow + grid texture still render on top to preserve the FBK signature.
   * Used by the Matchday card's "split" theme to two-tone the background.
   */
  customBaseGradient?: string;
  /**
   * When true, the header (wordmark + pill) flips to dark colors for legibility
   * on light backgrounds. Used by the Matchday card's "editorial" theme.
   */
  headerOnLight?: boolean;
  /**
   * When true, suppresses the gold sunrise + center halo + white grid overlays.
   * Used by themes (editorial, halftone, stripes) where those would clash.
   */
  suppressBrandOverlays?: boolean;
  /** Hide the footer entirely (for full-bleed cards). */
  hideFooter?: boolean;
  /**
   * Readability panel drawn above the background image and below content.
   * Lives here rather than inside a card body because it must span the true
   * card box — a child of the padded content area can only fake full-bleed
   * with negative insets, which throws off the gradient's percentage stops.
   */
  scrim?: ScrimSettings;
  /**
   * Escape hatch for decoration that must span the true card box rather than
   * the padded content area — colour panels, bands, borders. Rendered above
   * the background + scrim and below the header/content/footer, so content
   * always stays on top of it.
   */
  fullBleedLayer?: ReactNode;
}

const FbkCardShell = forwardRef<HTMLDivElement, FbkCardShellProps>(
  function FbkCardShell(
    {
      format,
      children,
      footerLeft,
      footerRight,
      header,
      background,
      customBaseGradient,
      headerOnLight,
      suppressBrandOverlays,
      hideFooter,
      scrim,
      fullBleedLayer,
    },
    ref,
  ) {
    const wordmark = header?.wordmark ?? "FansBall Knowledge";
    const logoUrl = header?.logoUrl ?? "/logo.png";
    const pillText = header?.pillText ?? "WORLD CUP 2026";
    const hideHeader = header?.hide === true;
    const spec = CARD_FORMATS[format];

    const rootStyle: CSSProperties = {
      width: spec.width,
      height: spec.height,
      padding: spec.padding,
      position: "relative",
      overflow: "hidden",
      fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
      fontWeight: 800,
      color: "#FFFFFF",
      background:
        customBaseGradient ||
        "linear-gradient(135deg, #0D8C3C 0%, #0D5C2E 60%, #07321A 100%)",
      display: "flex",
      flexDirection: "column",
      boxSizing: "border-box",
    };

    // Layer 2 + 3: gold sunrise glow (upper-left) + center halo
    const glowStyle: CSSProperties = {
      position: "absolute",
      inset: 0,
      pointerEvents: "none",
      backgroundImage: [
        "radial-gradient(1200px 600px at 20% 0%, rgba(255, 215, 0, 0.14), transparent 60%)",
        "radial-gradient(circle at 50% 40%, rgba(255, 215, 0, 0.16), transparent 60%)",
      ].join(", "),
    };

    // Layer 4: white grid pattern (the signature "checkbox" texture)
    const gridStyle: CSSProperties = {
      position: "absolute",
      inset: 0,
      pointerEvents: "none",
      opacity: 0.07,
      backgroundImage: [
        "linear-gradient(white 1px, transparent 1px)",
        "linear-gradient(90deg, white 1px, transparent 1px)",
      ].join(", "),
      backgroundSize: "70px 70px",
    };

    const headerIconSize = format === "story" ? 64 : 56;
    const headerTextSize = format === "story" ? 30 : 26;
    const pillFontSize = format === "story" ? 16 : 15;
    const pillPaddingY = format === "story" ? 14 : 12;
    const pillPaddingX = format === "story" ? 28 : 24;
    const footerFontSize = format === "story" ? 20 : 18;

    return (
      <div ref={ref} style={rootStyle}>
        {/* Optional user background image — sits BETWEEN base gradient and gold glow
            so the brand signature still bleeds through.

            When fitting the whole image (`contain`), a blurred copy fills the
            letterbox gaps first so the photo reads as part of the card rather
            than a rectangle pasted on top of it. The blurred layer is always
            `cover` — it exists to fill space, not to be looked at. */}
        {background?.imageUrl &&
          background.fit === "contain" &&
          background.blurBackdrop !== false && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                backgroundImage: `url(${background.imageUrl})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                // Scale up slightly so the blur radius doesn't bleed the
                // edges inward and leave a soft rim against the card border.
                transform: "scale(1.12)",
                filter: "blur(38px) brightness(0.45) saturate(1.1)",
                opacity: background.opacity ?? 0.3,
                pointerEvents: "none",
              }}
            />
          )}
        {background?.imageUrl && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage: `url(${background.imageUrl})`,
              backgroundSize: background.fit === "contain" ? "contain" : "cover",
              backgroundPosition:
                background.focal === "top"
                  ? "top center"
                  : background.focal === "bottom"
                    ? "bottom center"
                    : "center",
              backgroundRepeat: "no-repeat",
              opacity: background.opacity ?? 0.3,
              pointerEvents: "none",
            }}
          />
        )}
        {/* Readability scrim — above the photo, below every brand overlay and
            all content. Percentage stops map to true card height because this
            sits at inset 0 on the root. */}
        {scrim && (scrim.strength ?? 0) > 0 && (
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              background: (() => {
                const strength = Math.min(Math.max(scrim.strength ?? 0, 0), 1);
                const height = Math.min(Math.max(scrim.height ?? 0.45, 0), 1);
                // Start the ramp `height` up from the bottom and ease in, so
                // there is no visible line where the scrim begins.
                const start = Math.round((1 - height) * 100);
                const mid = Math.round((1 - height * 0.45) * 100);
                return `linear-gradient(180deg,
                  transparent 0%,
                  transparent ${start}%,
                  rgba(0,0,0,${(strength * 0.45).toFixed(3)}) ${mid}%,
                  rgba(0,0,0,${strength.toFixed(3)}) 100%)`;
              })(),
            }}
          />
        )}
        {fullBleedLayer}
        {!suppressBrandOverlays && <div style={glowStyle} />}
        {!suppressBrandOverlays && <div style={gridStyle} />}

        {/* HEADER */}
        {!hideHeader && (
          <div
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              zIndex: 2,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <img
                src={logoUrl}
                alt="FBK"
                crossOrigin={logoUrl.startsWith("data:") || logoUrl.startsWith("/") ? undefined : "anonymous"}
                style={{
                  width: headerIconSize,
                  height: headerIconSize,
                  borderRadius: 12,
                  objectFit: "cover",
                  background: headerOnLight
                    ? "rgba(7,50,26,0.08)"
                    : "rgba(255,255,255,0.08)",
                  border: headerOnLight
                    ? "1px solid rgba(7,50,26,0.18)"
                    : "1px solid rgba(255,255,255,0.18)",
                }}
              />
              <div
                style={{
                  fontSize: headerTextSize,
                  fontWeight: 800,
                  lineHeight: 1,
                  letterSpacing: "-0.01em",
                  color: headerOnLight ? "#07321A" : "#FFFFFF",
                }}
              >
                {wordmark}
              </div>
            </div>

            {pillText && (
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: `${pillPaddingY}px ${pillPaddingX}px`,
                  borderRadius: 9999,
                  border: headerOnLight
                    ? "1px solid rgba(7,50,26,0.20)"
                    : "1px solid rgba(255,255,255,0.20)",
                  background: headerOnLight
                    ? "rgba(7,50,26,0.08)"
                    : "rgba(255,255,255,0.10)",
                  fontSize: pillFontSize,
                  fontWeight: 800,
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  whiteSpace: "nowrap",
                  color: headerOnLight ? "#07321A" : "#FFFFFF",
                }}
              >
                {pillText}
              </div>
            )}
          </div>
        )}

        {/* MAIN CONTENT */}
        <div
          style={{
            position: "relative",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            zIndex: 2,
            minHeight: 0,
          }}
        >
          {children}
        </div>

        {/* FOOTER — flips to dark text on light themes (Editorial cream,
            Minimal off-white, Ignite gold) so it doesn't disappear. Reuses
            the same `headerOnLight` flag that already drives the header. */}
        {!hideFooter && (footerLeft || footerRight) && (
          <div
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              zIndex: 2,
              fontSize: footerFontSize,
              fontWeight: 700,
            }}
          >
            <div
              style={{
                color: headerOnLight ? "rgba(26,46,31,0.85)" : "rgba(255,255,255,0.85)",
              }}
            >
              {footerLeft}
            </div>
            <div
              style={{
                color: headerOnLight ? "rgba(26,46,31,0.7)" : "rgba(255,255,255,0.70)",
                fontFamily:
                  "ui-monospace, 'JetBrains Mono', 'SF Mono', Menlo, monospace",
                letterSpacing: "0.2em",
                textTransform: "uppercase",
              }}
            >
              {footerRight}
            </div>
          </div>
        )}
      </div>
    );
  },
);

export default FbkCardShell;
